import qs, { ParsedQs } from 'qs';
import { useReducer, useEffect, Dispatch } from 'react';

const history = typeof window !== 'undefined' ? window.history : ({} as History);
const useHash = !('pushState' in history);
function getQueryState() {
  const { search, hash } = window.location;
  const query = qs.parse(useHash ? hash.slice(1) : search, {
    comma: true,
    ignoreQueryPrefix: true
  });

  return query;
}

export type QueryState = ParsedQs;
export type MergeQueryState<S, Q> = (queryState: Q, nextState: S) => Q;
export type ReadQueryState<S, Q> = (queryState: Q) => S;

export interface UseUrlStateOptions {
  /**
   * if true, the url will be added to history on state change
   *
   * if false, the url will replace the current url on state change
   *
   * @default true
   */
  addToHistory?: boolean;
  /**
   * if true, on state change, the url will be updated in the browser but the state will not be updated
   *
   * if false, on state change, the url will be updated in the browser and the state will be updated
   *
   * @default false
   *
   * @example <caption>Example Usage</caption>
   * const [state, setState] = useUrlState(read, merge, {readOnly: true});
   * // state will never be updated
   */
  readOnly?: boolean;
  /**
   * if true, the initial state will be synced to the url on mount
   *
   * if false, the state will be synced to the url on first state change
   *
   * @default false
   */
  syncInitialState?: boolean;
}

/**
 * useUrlState is a hook that allows you to read and update the url state
 *
 * It is lower level than useQueryState but provides more control over the state of the entire url's query string
 * @see {@link useQueryState} for simpler storing of state into the url
 * @param readFromQueryState - given the url's query object, return the state to be used
 * @param mergeQueryState - given the current url's query object and the next state, return the new query object
 * @param options - options for the useUrlState hook
 * @returns {[S, Dispatch<S>]} - the state and a dispatch function
 */
export default function useUrlState<S, Q extends QueryState>(
  readFromQueryState: ReadQueryState<S, Q>,
  mergeQueryState: MergeQueryState<S, Q>,
  { addToHistory = true, readOnly = false, syncInitialState = false }: UseUrlStateOptions = {}
): [S, Dispatch<S>] {
  // Effect to sync the state to the url
  const updateUrl = (nextState: S) => {
    const state = mergeQueryState(getQueryState() as Q, nextState);
    const queryString = qs.stringify(state, {
      addQueryPrefix: true,
      arrayFormat: 'comma'
    });

    // Only sync if there is something to sync
    if (
      (useHash ? window.location.hash : window.location.search).slice(1) ||
      queryString.length > 1
    ) {
      const { pathname } = window.location;

      if (useHash) {
        const hash = `#${queryString.slice(1)}`;

        if (addToHistory) {
          window.location.hash = hash;
        } else {
          window.location.replace(hash);
        }
      } else {
        const url = `${pathname}${queryString}`;

        if (addToHistory) {
          history.pushState({}, '', url);
        } else {
          history.replaceState({}, '', url);
        }
      }
    }

    return nextState;
  };

  // React 19 useReducer<S, I, A>: A is a tuple ([]|[any]), so the action args type is [S], not S.
  const [state, setState] = useReducer<S, Q, [S]>(
    (currentState, nextState) => {
      // We do this as part of the reducer instead of as an effect so that when only wanting to read the state, we don't have to re-render
      const mergedState = updateUrl(nextState);

      if (readOnly) {
        // If you only ever need this for a start state no need to re render
        return currentState;
      }

      return mergedState;
    },
    getQueryState() as Q,
    readFromQueryState
  );

  // [VB] Automatically set the query with current parameters,
  // applies when url needs to be updated with data from storage
  useEffect(() => {
    if (syncInitialState) {
      updateUrl(state);
    }
  });

  return [state, setState];
}
